/**
 * THE DAY-FILTER VOCABULARY AND THE ONE PREDICATE THAT READS IT.
 *
 * ⚠️ THIS FILE EXISTS BECAUSE THE RULE HAD FOUR IMPLEMENTATIONS AND ONE OF THEM WAS
 * DELETING THE USER'S DATA. Cycle 3 collapsed the two SERVER deciders —
 * `weekendExclusionSQL` (the WHERE clause) and `theDayTheHeadlineIsAbout`'s `admits` (the
 * single-day KPI's target day) — onto one mode resolution, and made an unknown mode a
 * compile error at both. It did not reach the browser. The chart padder in `TrendChart.tsx`
 * was the fourth, it read a BOOLEAN (`includeWeekends`), and a boolean cannot hold three
 * modes: `'weekdays'` and `'weekends'` both arrive as `false`. Under "Weekends only" at
 * daily granularity it therefore dropped every Saturday and Sunday — i.e. every bucket the
 * server had just returned — BEFORE looking at whether the bucket carried data, and padded
 * the range with synthetic Mon–Fri zeros. Measured on the shipped bytes, 2026-09-14:
 * 2 points and 21 requests in, 5 points and 0 requests out. The totals badge above the same
 * chart sums the UNPADDED series, so the card printed 21 and drew 0 two lines apart.
 *
 * So the rule lives here, in a module with NO IMPORTS, and everything that needs it — the
 * server and the browser bundle alike — reads this one function. A module with no imports
 * is the whole point: `src/services/analytics/filters.ts` cannot be reached from the SPA
 * (it pulls drizzle and the schema), which is how the browser came to own a copy.
 *
 * ⚠️ AND THAT SENTENCE WAS THE ONLY THING HOLDING IT UP UNTIL 2026-09-14. Adding
 * `import { langfuseTraces } from '../../db/schema.js';` here was `tsc` 0 and a green
 * suite, twice measured — the property this module exists FOR was pinned by nothing.
 * `tests/dashboard/trend-chart-day-filter.test.ts` now walks the import closure of every
 * server module the SPA reaches and reds on a bare specifier anywhere in it, naming the
 * path. It is not keyed to this file: the set is derived from the SPA's own imports, so a
 * second shared module is covered the day it is written.
 *
 * ⚠️ WHAT THIS DOES NOT REACH YET, named rather than implied. Two more hand-written
 * spellings of the same three words survive in the SPA and are one movement each:
 *   - `ActivityHeatmap.tsx` `visibleDows` — an `if`/`if`/return-everything CATCH-ALL, the
 *     shape cycle 3 removed from both server deciders. A fourth mode shows all seven rows.
 *   - `filters-schema.ts` — the `DayFilter` union and the `z.enum` beside it, written out
 *     by hand twice. `z.enum(DAY_FILTER_MODES)` would derive both.
 * Neither is touched here because neither is needed to fix the padder, and half-migrating a
 * rule leaves three shapes where there were two.
 */

/**
 * THE MODES, AS A VALUE — and the union is DERIVED from it, not written twice.
 *
 * ⚠️ 2026-09-13: this exists so a guard can quantify over the modes rather than over a
 * list of filter SHAPES. `dau-headline-day.test.ts` used to loop over seven objects
 * (`{dayFilter:'all'}`, `{includeWeekends:true}`, `{}`, `undefined`, …) and call its own
 * message «a mode was added and this case does not know it» — but no object in that list
 * can produce a mode nobody has written, so the case could not fire. A validator added a
 * fourth mode with a rule in `weekendExclusionSQL` and none in `theDayTheHeadlineIsAbout`
 * — the headline asking about a Tuesday while its own WHERE demanded a Monday, which is
 * D-11 exactly — and the file stayed 4/4 GREEN with `tsc` clean.
 *
 * Because the union is `(typeof DAY_FILTER_MODES)[number]`, a mode cannot enter the
 * vocabulary without entering this array, and the guard iterates the array.
 */
export const DAY_FILTER_MODES = ['all', 'weekdays', 'weekends'] as const;

export type DayFilterMode = (typeof DAY_FILTER_MODES)[number];

/**
 * THE MODE NOTHING DECIDES — a compile error, and, if it is ever cast past the compiler,
 * a throw.
 *
 * ⚠️ 2026-09-14. Both server deciders used to end on a CATCH-ALL that answered 'weekdays'.
 * That made a fourth mode silent in the worst possible way: `weekendExclusionSQL` and
 * `theDayTheHeadlineIsAbout` fell through TOGETHER, agreed with each other, and the guard
 * that pairs them stayed green — 6/6, with `tsc` clean — while the docblock beside it
 * promised that «a mode added there without a rule HERE reddens on the day it is added».
 * A guard that watches two sides for DISAGREEMENT cannot see them both falling through.
 *
 * An unknown mode is refused where it can be refused loudest: `DayFilterMode` is derived
 * from {@link DAY_FILTER_MODES}, so adding a mode to that array leaves every switch over
 * it non-exhaustive and `tsc --noEmit` fails on the line below, naming the mode.
 */
export function theModeNothingDecides(mode: never): never {
  throw new Error(`unhandled day-filter mode: ${JSON.stringify(mode)} — give it a rule in `
    + 'weekendExclusionSQL AND in theDayTheHeadlineIsAbout, which must agree');
}

/**
 * WHICH DAYS OF THE WEEK A MODE ADMITS, on a Postgres `extract(dow from …)` numbering
 * (0 = Sunday … 6 = Saturday) — which is the same numbering as JavaScript's
 * `Date#getUTCDay()`, and the reason this one function can answer for both the SQL side
 * and the chart side.
 *
 * ⚠️ NO CATCH-ALL, deliberately, and the `default` arm is not decoration: a mode cast past
 * the compiler THROWS here rather than being answered as 'weekdays'.
 *
 * WHEN EACH OF THOSE TWO FIRES, read off `.gitlab-ci.yml` rather than assumed: the server's
 * `npm run build:typecheck` runs in the `test` job, which is MR-only, so a fourth mode in
 * `DAY_FILTER_MODES` reddens the merge request. The SPA is type-checked too — `tsc -b`
 * inside `npm run build:frontend` — but only in the `build` job, whose rule is
 * `$CI_COMMIT_BRANCH == "master"`: it runs AFTER the gate, on the branch, where it blocks
 * the deploy instead of the merge. And the SPA's own test files run NOWHERE. So on the
 * browser side the throw is what fires first, and on an MR it is what fires at all.
 *
 * Pure, total over the union, and independent of any clock or locale.
 */
export function theDayFilterAdmits(mode: DayFilterMode, dow: number): boolean {
  switch (mode) {
    case 'all': return true;
    case 'weekends': return dow === 0 || dow === 6;
    case 'weekdays': return dow !== 0 && dow !== 6;
    default: return theModeNothingDecides(mode);
  }
}
